<template>
  <!--LAYBY LANDING PAGE: Story CD-19 -->
  <PageHeader title="Lay-by Purchases" v-model="selectedState" :options="stateOptions" placeholder="All Lay-by" />
  <div class="w-full flex items-center justify-center" v-if="isLoading">
    <CustomSkeleton class="rounded-full" />
  </div>
  <div v-else-if="laybyOrders.length > 0" :class="{ 'pb-[70px] px-2': !isDesktop }">
    <div class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grip-cols-4 mb-4">
      <LaybyCard v-for="(order, index) in laybyOrders" :order="order" :key="index" />
    </div>

    <PaginationControls
      :total-count="totalCount"
      :current-page="pageNumber"
      :page-size="generalStore.paymentPlanPageSize"
      :page-size-options="generalStore.getPaymentPlanPageSizeOptions()"
      @update:page="onUpdatePage"
      @update:pageSize="onUpdatePageSize"
    />
  </div>
  <NoActiveOrders
    v-else
    :title="'No Lay-by Found'"
    @click="router.push({ name: 'shopperStores' })"
    :description="'Start a lay-by and view it here'"
    :button-text="'Start Lay-by'"
  />
</template>

<script lang="ts" setup>
import CustomSkeleton from '@/components/common/CustomSkeleton.vue';
import NoActiveOrders from '@/components/common/NoActiveOrders.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
import LaybyCard from '@/components/shopper/lay-by/LaybyCard.vue';
import { NetworkError } from '@/errors';
import type { OrderServiceGetManyRequest } from '@/models/orders';
import { fetchOrders } from '@/services/orders';
import { useGeneralStore } from '@/stores/general';
import { useOrderStore } from '@/stores/orders';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { isPaymentPlanMerchant } from '@/utils/paymentPlanMerchants.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

const generalStore = useGeneralStore();
const orderStore = useOrderStore();
const { isDesktop } = useBreakpoint();

const router = useRouter();

const laybyOrders = ref([] as ModelOrderResponse[]);
const pageNumber = ref(1);
const isLoading = ref(false);
const selectedState = ref('all');
const totalCount = ref(0);
const useStoreData = ref(true);
const orderParams = ref<OrderServiceGetManyRequest>({
  populate: ['voucher', 'plans', 'plans.benefactor'],
  type: 'LAYBY',
});

const storeLaybyOrders = computed(() =>
  orderStore.allOrders.filter((o) => o.type === 'LAYBY' && !isPaymentPlanMerchant(o)),
);

const stateOptions = [
  { value: 'all', label: 'All purchases' },
  { value: 'PLACED', label: 'Active purchases' },
  { value: 'CANCELLED', label: 'Cancelled purchases' },
  { value: 'CANCELLING', label: 'Cancelling purchases' },
  { value: 'COMPLETED', label: 'Completed purchases' },
  { value: 'SUSPENDED', label: 'Suspended purchases' },
];

const getLaybyOrders = async () => {
  isLoading.value = true;
  try {
    orderParams.value.limit = generalStore.paymentPlanPageSize;
    orderParams.value.skip = (pageNumber.value - 1) * generalStore.paymentPlanPageSize;

    if (selectedState.value !== 'all') {
      orderParams.value.state = selectedState.value as any;
    } else {
      delete orderParams.value.state;
    }

    const { orders, totalCount: count } = await fetchOrders(orderParams.value);

    laybyOrders.value = (orders || []).filter((o) => !isPaymentPlanMerchant(o));
    totalCount.value = count || 0;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('shopper-layby', { status: 'network_error', channel: 'shopper' });
    } else {
      createException(error);
      addMetric('shopper-layby', { status: 'error', channel: 'shopper' });
    }
  } finally {
    isLoading.value = false;
  }
};

watch(selectedState, () => {
  useStoreData.value = false;
  pageNumber.value = 1;
  getLaybyOrders();
});

const onUpdatePage = (page: number) => {
  useStoreData.value = false;
  pageNumber.value = page;
  getLaybyOrders();
};

const onUpdatePageSize = (size: number) => {
  useStoreData.value = false;
  generalStore.paymentPlanPageSize = size;
  pageNumber.value = 1;
  getLaybyOrders();
};

const init = async () => {
  if (orderStore.allOrdersFetched) {
    laybyOrders.value = storeLaybyOrders.value;
    totalCount.value = storeLaybyOrders.value.length;
  } else {
    await getLaybyOrders();
  }
};

init();
</script>
